import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, debounceTime, filter, map } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { getPicksCount, isValidOnePickCombo } from '../../../core/utils';
import BetslipPickActions from '../../picks/actions';
import { removePicksFromGroup } from '../../picks/grouped-picks.actions';
import { betslipPicksListCountSelector, betslipPicksListSelector, selectBetBuilderPicksInfo } from '../../picks/selectors';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import { BetslipTypesActions } from '../../types/actions';
import { betslipTypeStateSelector } from '../../types/selectors';
import BetValidationService from '../../validation/services/composition/bets/bet-validation.service';
import { selectIsQuickBetActive } from '../quick-bet.selectors';

@Injectable()
export default class QuickBetLinearEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private betValidationService: BetValidationService,
        private betslipPickUtils: BetslipPickUtilsService,
        private betslipConfig: BetslipConfig,
    ) {}

    //in case of linear when user add stake we update selectedByUser to true,
    //if we are on quickbet we would like to still switch the tabs to combo or single
    //when user adds or removes pick(s)

    autoSwitchToCombo$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipTypesActions.triggerAutoComboSwitch),
            concatLatestFrom(() => [
                this.store.select(betslipPicksListSelector),
                this.store.select(betslipTypeStateSelector),
                this.store.select(selectBetBuilderPicksInfo),
                this.store.select(selectIsLinearBetslip),
                this.store.select(selectIsQuickBetActive),
            ]),
            filter(
                ([, , { base }, isLinear, isQuickBet]) =>
                    base.selectedByUser &&
                    isLinear &&
                    isQuickBet &&
                    (base.currentSelectedType === BetslipType.Single || base.currentSelectedType === BetslipType.Combo),
            ),
            concatMap(function* ([, picks, typesState, betBuilderPicksState, betslipBarEnabled]): Iterable<Action> {
                //we are getting selected picks and groups because we want to deal with groups as if they were always selected (to avoid infinity loop in this effect)
                const selectedPicks = effect.betslipPickUtils.getSelectedPicksAndGroups(
                    typesState,
                    picks,
                    effect.betslipConfig.isSportcastAsComboEnabled,
                );

                const hasComboPreventionErrors = !effect.betValidationService.checkComboBet(selectedPicks);
                const isValidOnePickComboState = isValidOnePickCombo(selectedPicks, effect.betslipConfig.isSportcastAsComboEnabled);
                const hasValidCount = getPicksCount(selectedPicks) > 1 || isValidOnePickComboState;
                const betBarShouldShowCombo = hasValidCount && betslipBarEnabled;

                if (
                    ((!hasComboPreventionErrors && hasValidCount) || betBarShouldShowCombo) &&
                    typesState.base.currentSelectedType === BetslipType.Single
                ) {
                    // Single bet selected and has not errors  => select combo tab
                    yield BetslipTypesActions.setSelectedType({
                        betslipType: BetslipType.Combo,
                        selectedByUser: typesState.base.selectedByUser,
                        systemInfo: null,
                    });
                }
                if (
                    (hasComboPreventionErrors || !hasValidCount) &&
                    !betBarShouldShowCombo &&
                    typesState.base.currentSelectedType === BetslipType.Combo &&
                    (!effect.betslipConfig.isSportcastAsComboEnabled || !betBuilderPicksState.hasOnlyBetBuilderPicks)
                ) {
                    // Combo bet selected and has errors  => select single tab
                    yield BetslipTypesActions.setSelectedType({
                        betslipType: BetslipType.Single,
                        selectedByUser: typesState.base.selectedByUser,
                        systemInfo: null,
                    });
                }
            }),
        );
    });

    autoSwitchToSingle$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, removePicksFromGroup, BetslipPickActions.removeMultiplePicks),
            //add debounce to avoid redundant effect triggers in case multiple remove picks were triggered in short cycles (removing picks when creating a group for example)
            debounceTime(5),
            concatLatestFrom(() => this.store.select(selectBetBuilderPicksInfo)),
            concatLatestFrom(() => this.store.select(betslipPicksListCountSelector)),
            map(([[_, betBuilderPicksState], length]) =>
                effect.betslipConfig.isSportcastAsComboEnabled
                    ? betBuilderPicksState.nonBetBuilderPicksCount === 1 && betBuilderPicksState.betBuilderPicksCount === 0
                    : length === 1,
            ),
            filter((isValidPickCountForSingle) => isValidPickCountForSingle),
            concatLatestFrom(() => [
                this.store.select(betslipTypeStateSelector),
                this.store.select(selectIsLinearBetslip),
                this.store.select(selectIsQuickBetActive),
            ]),
            filter(([_, types, isLinear, isQuickBet]) => !types.base.currentSelectedType || (types.base.selectedByUser && isLinear && isQuickBet)),
            map(([, typesState]) =>
                BetslipTypesActions.setSelectedType({
                    betslipType: BetslipType.Single,
                    selectedByUser: typesState.base.selectedByUser,
                    systemInfo: null,
                }),
            ),
        );
    });
}
